import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import systemNotificationsActions from '../../store/systemNotificationsStore/systemNotificationsActions';
import styles from './SystemNotificationModal.module.scss';

const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 1000,
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: 'none',
        padding: 0,
        borderRadius: 16,
        maxWidth: '600px',
        width: '90%',
        maxHeight: '85vh',
        overflow: 'auto',
    },
};

const SystemNotificationModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { campaigns, notification, submitStatus } = useSelector(
        state => state.systemNotificationsReducer
    );
    const branchId = useSelector(state => state.branchReducer.info?.id);

    const currentCampaign = campaigns[0] || null;

    const [answers, setAnswers] = useState({});
    const [showThankYou, setShowThankYou] = useState(false);
    const [isLocallyHidden, setIsLocallyHidden] = useState(false);

    const prevSubmitStatus = useRef('');
    const thankYouTimer = useRef(null);

    const closeThankYou = () => {
        clearTimeout(thankYouTimer.current);
        setShowThankYou(false);
    };

    // Show thank-you only on a fresh PENDING → SUCCESS transition
    useEffect(() => {
        if (prevSubmitStatus.current === 'PENDING' && submitStatus === 'SUCCESS') {
            setShowThankYou(true);
            thankYouTimer.current = setTimeout(closeThankYou, 2500);
            return () => clearTimeout(thankYouTimer.current);
        }
        prevSubmitStatus.current = submitStatus;
    }, [submitStatus]);

    // Reset answers when the notification changes (next campaign)
    useEffect(() => {
        setAnswers({});
    }, [notification?.id]);

    const questions = notification?.poll?.questions || [];
    const isSubmitting = submitStatus === 'PENDING';

    const isOpen = showThankYou || (!!currentCampaign && !!notification && !isLocallyHidden);

    const handleRateChange = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: { type: 'rate', rate: value } }));
    };

    const handleSingleChoiceChange = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: { type: 'single-choice', value } }));
    };

    const handleMultipleChoiceChange = (questionId, option, checked) => {
        setAnswers(prev => {
            const current = prev[questionId]?.value || [];
            const updated = checked
                ? [...current, option]
                : current.filter(v => v !== option);
            return { ...prev, [questionId]: { type: 'multiple-choice', value: updated } };
        });
    };

    const handleOpenEndedChange = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: { type: 'open-ended', value } }));
    };

    const buildAnswersPayload = () => {
        return questions
            .map(q => {
                const answer = answers[q.id];
                if (!answer) return null;
                if (q.type === 'rate') {
                    return { question_id: q.id, rate: answer.rate };
                }
                if (q.type === 'multiple-choice') {
                    return { question_id: q.id, values: answer.value };
                }
                return { question_id: q.id, value: answer.value };
            })
            .filter(Boolean);
    };

    const handleSubmit = () => {
        if (!currentCampaign || !branchId) return;
        dispatch(systemNotificationsActions.respondToCampaign(
            branchId,
            currentCampaign.id,
            { answers: buildAnswersPayload() }
        ));
    };

    const handleLater = () => {
        setIsLocallyHidden(true);
    };

    const renderQuestion = (question) => {
        switch (question.type) {
            case 'rate': {
                const min = question.min_rate || 1;
                const max = question.max_rate || 5;
                const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
                const selected = answers[question.id]?.rate;
                return (
                    <div className={styles.rateButtons}>
                        {steps.map(val => (
                            <button
                                key={val}
                                type="button"
                                className={`${styles.rateButton} ${selected === val ? styles.rateButtonSelected : ''}`}
                                onClick={() => handleRateChange(question.id, val)}
                                disabled={isSubmitting}
                            >
                                {val}
                            </button>
                        ))}
                    </div>
                );
            }

            case 'single-choice': {
                const options = question.options || [];
                return (
                    <div className={styles.optionsContainer}>
                        {options.map(option => (
                            <label key={option} className={styles.optionLabel}>
                                <input
                                    type="radio"
                                    name={question.id}
                                    value={option}
                                    checked={answers[question.id]?.value === option}
                                    onChange={() => handleSingleChoiceChange(question.id, option)}
                                    className={styles.radioInput}
                                    disabled={isSubmitting}
                                />
                                <span className={styles.optionText}>{option}</span>
                            </label>
                        ))}
                    </div>
                );
            }

            case 'multiple-choice': {
                const options = question.options || [];
                const selected = answers[question.id]?.value || [];
                return (
                    <div className={styles.optionsContainer}>
                        {options.map(option => (
                            <label key={option} className={styles.optionLabel}>
                                <input
                                    type="checkbox"
                                    value={option}
                                    checked={selected.includes(option)}
                                    onChange={e => handleMultipleChoiceChange(question.id, option, e.target.checked)}
                                    className={styles.checkboxInput}
                                    disabled={isSubmitting}
                                />
                                <span className={styles.optionText}>{option}</span>
                            </label>
                        ))}
                    </div>
                );
            }

            case 'open-ended': {
                return (
                    <textarea
                        className={styles.textarea}
                        value={answers[question.id]?.value || ''}
                        onChange={e => handleOpenEndedChange(question.id, e.target.value)}
                        rows={3}
                        placeholder={t('SystemNotification.openEndedPlaceholder')}
                        disabled={isSubmitting}
                    />
                );
            }

            default:
                return null;
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            ariaHideApp={false}
            isOpen={isOpen}
            style={modalStyles}
            onRequestClose={showThankYou ? closeThankYou : undefined}
        >
            {showThankYou ? (
                <div className={styles.thankYouModal}>
                    <div className={styles.thankYouIcon}>✓</div>
                    <h3 className={styles.thankYouTitle}>{t('SystemNotification.thankYouTitle')}</h3>
                    <p className={styles.thankYouMessage}>{t('SystemNotification.thankYouMessage')}</p>
                    <button className={styles.thankYouCloseButton} onClick={closeThankYou}>
                        {t('SystemNotification.close')}
                    </button>
                </div>
            ) : (
                <div className={styles.modal}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>{notification.title}</h2>
                    </div>

                    <div className={styles.body}>
                        <p className={styles.message}>{notification.message}</p>

                        {notification.links && notification.links.length > 0 && (
                            <div className={styles.links}>
                                {notification.links.map((link, index) => (
                                    <a
                                        key={index}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.link}
                                    >
                                        {link.label}
                                    </a>
                                ))}
                            </div>
                        )}

                        {questions.length > 0 && (
                            <div className={styles.poll}>
                                {questions.map((question, index) => (
                                    <div key={question.id} className={styles.question}>
                                        <p className={styles.questionText}>
                                            <span className={styles.questionNumber}>{index + 1}.</span>
                                            {question.text}
                                        </p>
                                        {renderQuestion(question)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={styles.footer}>
                        {questions.length > 0 && (
                            <button
                                className={styles.laterButton}
                                onClick={handleLater}
                                disabled={isSubmitting}
                            >
                                {t('SystemNotification.later')}
                            </button>
                        )}
                        <button
                            className={styles.submitButton}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? t('SystemNotification.submitting')
                                : questions.length === 0
                                    ? t('SystemNotification.close')
                                    : t('SystemNotification.submit')}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default SystemNotificationModal;
